import {
  calculateAccuracy,
  calculateAccurateWpm,
  calculateConsistencyFromPaceSamples,
  calculateFinalTextAccuracy,
  calculateNetWpm,
  calculateRawWpm,
  calculateRollingPaceSamples,
  evaluateSessionValidity,
  withValidityOverrides,
} from "./metrics";
import {
  DEFAULT_MEASUREMENT_CONFIG,
  type KeystrokeEvent,
  type MeasurementConfig,
  type NormalizedInputType,
  type TypingMetricSnapshot,
  type TypingSessionOptions,
  type TypingSessionSummary,
} from "./types";

interface TextChange {
  start: number;
  inserted: string;
  removed: string;
}

function randomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `typing-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function findTextChange(previous: string, current: string): TextChange {
  let prefixLength = 0;
  while (
    prefixLength < previous.length &&
    prefixLength < current.length &&
    previous[prefixLength] === current[prefixLength]
  ) {
    prefixLength += 1;
  }

  let suffixLength = 0;
  while (
    suffixLength < previous.length - prefixLength &&
    suffixLength < current.length - prefixLength &&
    previous[previous.length - 1 - suffixLength] ===
      current[current.length - 1 - suffixLength]
  ) {
    suffixLength += 1;
  }

  return {
    start: prefixLength,
    inserted: current.slice(prefixLength, current.length - suffixLength),
    removed: previous.slice(prefixLength, previous.length - suffixLength),
  };
}

function normalizeInputType(inputType?: string): NormalizedInputType {
  switch (inputType) {
    case "insertText":
    case "insertCompositionText":
    case "deleteBackward":
    case "deleteForward":
    case "insertFromPaste":
    case "compositionStart":
    case "compositionUpdate":
    case "compositionEnd":
      return inputType;
    case "deleteContentBackward":
      return "deleteBackward";
    case "deleteContentForward":
      return "deleteForward";
    default:
      return "other";
  }
}

function isCompositionInput(inputType: NormalizedInputType): boolean {
  return (
    inputType === "insertCompositionText" ||
    inputType === "compositionStart" ||
    inputType === "compositionUpdate" ||
    inputType === "compositionEnd"
  );
}

export class TypingSession {
  readonly id: string;
  readonly sourceText: string;
  readonly sessionType: TypingSessionOptions["sessionType"];
  readonly contentApproved: boolean;
  readonly config: MeasurementConfig;

  private readonly clock: () => number;
  private readonly wallClock: () => number;
  private events: KeystrokeEvent[] = [];
  private currentValue = "";
  private firstInputAtMs: number | null = null;
  private startedAtIso: string | null = null;
  private completedAtMs: number | null = null;
  private completedAtIso: string | null = null;
  private pausedAtMs: number | null = null;
  private totalPausedMs = 0;
  private excludedIdleMs = 0;
  private lastScoredInputAtMs: number | null = null;
  private initialErrorPositions = new Set<number>();
  private correctedErrorPositions = new Set<number>();
  private pasteDetected = false;
  private timingDiscontinuityDetected = false;
  private completed = false;

  constructor(options: TypingSessionOptions) {
    this.id = options.id ?? randomId();
    this.sourceText = options.sourceText;
    this.sessionType = options.sessionType ?? "practice";
    this.contentApproved = options.contentApproved ?? false;
    this.clock = options.clock ?? (() => performance.now());
    this.wallClock = options.wallClock ?? (() => Date.now());
    this.config = withValidityOverrides(
      {
        ...DEFAULT_MEASUREMENT_CONFIG,
        ...options.config,
      },
      options.minimumDurationMs,
      options.minimumAttempts,
    );
  }

  get value(): string {
    return this.currentValue;
  }

  get keystrokes(): readonly KeystrokeEvent[] {
    return this.events;
  }

  get isPaused(): boolean {
    return this.pausedAtMs !== null;
  }

  get isCompleted(): boolean {
    return this.completed;
  }

  pause(timestampMs = this.clock()): void {
    if (this.pausedAtMs === null && !this.completed) {
      this.pausedAtMs = timestampMs;
    }
  }

  resume(timestampMs = this.clock()): void {
    if (this.pausedAtMs === null || this.completed) {
      return;
    }
    this.totalPausedMs += Math.max(0, timestampMs - this.pausedAtMs);
    this.pausedAtMs = null;
    this.lastScoredInputAtMs = timestampMs;
  }

  markCompleted(timestampMs = this.clock()): TypingSessionSummary {
    if (this.pausedAtMs !== null) {
      this.resume(timestampMs);
    }
    this.completed = true;
    this.completedAtMs = timestampMs;
    this.completedAtIso = new Date(this.wallClock()).toISOString();
    return this.summary(timestampMs);
  }

  recordValue(
    nextValue: string,
    rawInputType = "insertText",
    timestampMs = this.clock(),
  ): TypingMetricSnapshot {
    if (this.completed || this.pausedAtMs !== null) {
      return this.snapshot(timestampMs);
    }

    const inputType = normalizeInputType(rawInputType);
    const change = findTextChange(this.currentValue, nextValue);
    const isBulkInsertion =
      change.inserted.length > 1 && !isCompositionInput(inputType);
    const isPaste = inputType === "insertFromPaste" || isBulkInsertion;

    if (isPaste) {
      this.pasteDetected = true;
    }

    if (change.inserted.length > 0 && !isPaste) {
      if (this.firstInputAtMs === null) {
        this.firstInputAtMs = timestampMs;
        this.startedAtIso = new Date(this.wallClock()).toISOString();
      }

      if (this.lastScoredInputAtMs !== null) {
        const gap = timestampMs - this.lastScoredInputAtMs;
        if (gap < 0 || gap > this.config.timingDiscontinuityMs) {
          this.timingDiscontinuityDetected = true;
        } else if (gap > this.config.idleThresholdMs) {
          this.excludedIdleMs += Math.max(0, gap - this.config.idleGraceMs);
        }
      }

      for (let offset = 0; offset < change.inserted.length; offset += 1) {
        const sourceIndex = change.start + offset;
        const actual = change.inserted[offset] ?? null;
        const expected = this.sourceText[sourceIndex] ?? null;
        const wasPreviouslyIncorrect =
          this.initialErrorPositions.has(sourceIndex);
        const isCorrectAtEntry = actual === expected;

        if (!isCorrectAtEntry) {
          this.initialErrorPositions.add(sourceIndex);
        } else if (wasPreviouslyIncorrect) {
          this.correctedErrorPositions.add(sourceIndex);
        }

        this.events.push({
          sessionId: this.id,
          sequence: this.events.length + 1,
          timestampMs,
          activeElapsedMs: this.activeDuration(timestampMs),
          inputType,
          expected,
          actual,
          sourceIndex,
          isCorrectAtEntry,
          isCorrection: isCorrectAtEntry && wasPreviouslyIncorrect,
          wasPreviouslyIncorrect,
        });
      }
      this.lastScoredInputAtMs = timestampMs;
    }

    this.currentValue = nextValue;
    return this.snapshot(timestampMs);
  }

  snapshot(timestampMs = this.clock()): TypingMetricSnapshot {
    const activeDurationMs = this.activeDuration(timestampMs);
    const printableAttempts = this.events.length;
    const correctPrintableAttempts = this.events.filter(
      (event) => event.isCorrectAtEntry,
    ).length;
    const uncorrectedErrors = this.countCurrentUncorrectedErrors();
    const rawWpm = calculateRawWpm(
      printableAttempts,
      activeDurationMs,
      this.config.provisionalMetricsAfterMs,
    );
    const accurateWpm = calculateAccurateWpm(
      correctPrintableAttempts,
      activeDurationMs,
      this.config.provisionalMetricsAfterMs,
    );
    const correctEventTimes = this.events
      .filter((event) => event.isCorrectAtEntry)
      .map((event) => event.activeElapsedMs);
    const paceSamples = calculateRollingPaceSamples(
      correctEventTimes,
      activeDurationMs,
      this.config.consistencyWindowMs,
      this.config.consistencyStepMs,
    );
    const validity = evaluateSessionValidity({
      sessionType: this.sessionType ?? "practice",
      activeDurationMs,
      printableAttempts,
      accurateWpm,
      pasteDetected: this.pasteDetected,
      timingDiscontinuityDetected: this.timingDiscontinuityDetected,
      contentApproved: this.contentApproved,
      completed: this.completed,
      config: this.config,
    });

    return {
      activeDurationMs,
      printableAttempts,
      correctPrintableAttempts,
      initialErrors: this.events.filter(
        (event) => event.isCorrectAtEntry === false,
      ).length,
      correctedInitialErrors: this.correctedErrorPositions.size,
      uncorrectedErrors,
      rawWpm,
      accurateWpm,
      netWpm: calculateNetWpm(rawWpm, uncorrectedErrors, activeDurationMs),
      keystrokeAccuracy: calculateAccuracy(
        correctPrintableAttempts,
        printableAttempts,
      ),
      finalTextAccuracy: calculateFinalTextAccuracy(
        this.sourceText,
        this.currentValue,
        this.completed && this.currentValue.length >= this.sourceText.length,
      ),
      consistency: calculateConsistencyFromPaceSamples(
        paceSamples,
        this.config.consistencyMinimumSamples,
      ),
      isProvisional:
        activeDurationMs < this.config.practiceMinimumDurationMs ||
        printableAttempts < this.config.practiceMinimumAttempts,
      validity,
    };
  }

  summary(timestampMs = this.clock()): TypingSessionSummary {
    const snapshot = this.snapshot(timestampMs);
    return {
      ...snapshot,
      id: this.id,
      schemaVersion: 1,
      sessionType: this.sessionType ?? "practice",
      startedAt: this.startedAtIso,
      completedAt: this.completedAtIso,
      pausedDurationMs: this.totalPausedMs,
      excludedIdleDurationMs: this.excludedIdleMs,
      sourceLength: this.sourceText.length,
      finalTypedLength: this.currentValue.length,
      completed: this.completed,
      measurementVersion: "2.0.0",
    };
  }

  private activeDuration(timestampMs: number): number {
    if (this.firstInputAtMs === null) {
      return 0;
    }

    const effectiveEnd =
      this.completedAtMs ??
      (this.pausedAtMs === null ? timestampMs : this.pausedAtMs);
    return Math.max(
      0,
      effectiveEnd -
        this.firstInputAtMs -
        this.totalPausedMs -
        this.excludedIdleMs,
    );
  }

  private countCurrentUncorrectedErrors(): number {
    let incorrect = 0;
    for (
      let index = 0;
      index < Math.min(this.currentValue.length, this.sourceText.length);
      index += 1
    ) {
      if (this.currentValue[index] !== this.sourceText[index]) {
        incorrect += 1;
      }
    }
    return incorrect;
  }
}
